import React, { useState } from 'react';
import { X, ExternalLink, MapPin } from 'lucide-react';

const MAPBOX_TOKEN = 'pk.eyJ1Ijoibm9ibGVnbGl0Y2giLCJhIjoiY21uMzI4OG9yMWFlMTJ0b3RiM21jaXkycCJ9.WlazfNk0cPc0d-vYBX2g4w';

// Map POI type/class to an appropriate emoji
function poiEmoji(type: string): string {
  const t = (type || '').toLowerCase();
  if (/stadium|arena/.test(t)) return '🏟';
  if (/sport/.test(t)) return '🏋';
  if (/university|college/.test(t)) return '🎓';
  if (/school/.test(t)) return '🏫';
  if (/library/.test(t)) return '📚';
  if (/cafe|coffee/.test(t)) return '☕';
  if (/brewery|brew/.test(t)) return '🍺';
  if (/bar|pub/.test(t)) return '🍺';
  if (/restaurant|food|diner/.test(t)) return '🍽';
  if (/fast_food|burger/.test(t)) return '🍔';
  if (/hospital|clinic|medical/.test(t)) return '🏥';
  if (/pharmacy|drug/.test(t)) return '💊';
  if (/bank|atm/.test(t)) return '🏦';
  if (/hotel|motel|inn/.test(t)) return '🏨';
  if (/park|garden|nature/.test(t)) return '🌲';
  if (/church|cathedral|chapel|place_of_worship/.test(t)) return '⛪';
  if (/police/.test(t)) return '👮';
  if (/fire/.test(t)) return '🚒';
  if (/post/.test(t)) return '📮';
  if (/museum/.test(t)) return '🏛';
  if (/theatre|theater/.test(t)) return '🎭';
  if (/cinema|movie/.test(t)) return '🎬';
  if (/supermarket|grocery/.test(t)) return '🛒';
  if (/gas|fuel/.test(t)) return '⛽';
  if (/parking/.test(t)) return '🅿';
  if (/airport/.test(t)) return '✈';
  if (/bus|transit|train/.test(t)) return '🚌';
  return '📍';
}

interface BusinessBillboardProps {
  business: any;
  onClose: () => void;
  theme: any;
}

const BusinessBillboard: React.FC<BusinessBillboardProps> = ({ business, onClose, theme }) => {
  const name = business.properties.name || 'Unknown Location';
  const type = business.properties.type || business.properties.class || '';
  const emoji = poiEmoji(type);
  const [imgError, setImgError] = useState(false);

  let lat = '';
  let lng = '';
  if (business.geometry?.type === 'Point') {
    [lng, lat] = business.geometry.coordinates;
  }

  // Mapbox Static API — street-level 3D perspective (zoom 19, pitch 65°)
  const satelliteImg =
    lat && lng
      ? `https://api.mapbox.com/styles/v1/mapbox/streets-v12/static/${lng},${lat},19,0,65/480x280@2x?access_token=${MAPBOX_TOKEN}`
      : null;

  const query = lat && lng ? `${name} near ${lat},${lng}` : name;
  const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;

  const typeLabel = type.replace(/_/g, ' ') || 'Point of Interest';

  return (
    <div
      className="fixed top-20 right-4 z-[70] w-80 sm:w-96 overflow-hidden shadow-2xl"
      style={{
        background: 'rgba(4,1,8,0.92)',
        border: `1px solid ${theme.primary}55`,
        boxShadow: `0 0 28px ${theme.primary}18, 0 12px 40px rgba(0,0,0,0.7)`,
        borderRadius: 3,
        backdropFilter: 'blur(14px)',
      }}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3" style={{ borderBottom: `1px solid ${theme.primary}25` }}>
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-xl">{emoji}</span>
          <h2 className="font-orbitron text-sm font-bold truncate pr-2" style={{ color: theme.primary }}>
            {name}
          </h2>
        </div>
        <button
          onClick={onClose}
          className="shrink-0 opacity-60 hover:opacity-100 transition-opacity"
          style={{ color: theme.primary }}
        >
          <X size={18} />
        </button>
      </div>

      {/* Satellite photo */}
      {satelliteImg && !imgError ? (
        <div className="relative w-full" style={{ height: 200, background: '#0a0a14' }}>
          <img
            src={satelliteImg}
            alt={`Satellite view of ${name}`}
            className="w-full h-full object-cover"
            onError={() => setImgError(true)}
          />
          <div
            className="absolute bottom-2 right-2 px-2 py-0.5 text-[9px] font-mono rounded"
            style={{ background: 'rgba(4,1,8,0.7)', color: theme.primary, border: `1px solid ${theme.primary}30` }}
          >
            STREET VIEW
          </div>
        </div>
      ) : (
        <div className="w-full flex items-center justify-center" style={{ height: 200, background: '#0a0a14' }}>
          <span className="text-5xl opacity-30">{emoji}</span>
        </div>
      )}

      {/* Meta */}
      <div className="px-4 py-3 flex items-center gap-2">
        <MapPin size={13} style={{ color: theme.primary, flexShrink: 0 }} />
        <span className="text-[11px] text-amber-300/70 capitalize font-mono truncate">{typeLabel}</span>
        {lat && lng && (
          <span className="ml-auto text-[9px] text-amber-600/60 font-mono shrink-0">
            {parseFloat(lat).toFixed(4)}, {parseFloat(lng).toFixed(4)}
          </span>
        )}
      </div>

      {/* CTA */}
      <div className="px-4 pb-4">
        <a
          href={mapsUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center gap-2 w-full py-2 font-bold text-xs transition-opacity hover:opacity-80 font-mono uppercase tracking-widest"
          style={{
            background: `${theme.primary}22`,
            border: `1px solid ${theme.primary}44`,
            color: theme.primary,
            borderRadius: 2,
          }}
        >
          <ExternalLink size={13} /> Open in Google Maps
        </a>
      </div>
    </div>
  );
};

export default BusinessBillboard;
